import axios, { AxiosInstance } from "axios";
import { useQuery } from "react-query";
import { TopMenuProps } from "stv-utils-frontend";
import { Config, OrgId } from "@src/common/commonSlice";
import { useParams } from "react-router-dom";
import { useAppSelector } from "@src/reduxStore";
import { useIntl } from "react-intl";

interface NotFoundResponse {
  resource: string;
}

/**
 * Create Axios instance which is useful for RP DMR server requests.
 *
 * Handle redirections based on server access control.
 * - Redirect to login in case of HTTP 401 unauthorized response.
 * - Redirect to order-page in case of HTTP 402 missing active license.
 * - Redirect to permission-page in case of HTTP 403 unathorized request.
 * - Redirect to org not found -page in case of HTTP 404 org not found.
 */
const createRpApi = (): AxiosInstance => {
  const rpApi = axios.create();

  rpApi.interceptors.response.use(
    response => response,
    (error: unknown) => {
      if (axios.isAxiosError(error)) {
        /**
         * Not logged in.
         *
         * Store the attempted path into local storage
         * so we can redirect the user there after login.
         * The redirect will be done in /index.tsx
         */
        if (error.response?.status === 401) {
          window.localStorage.postLoginPath = window.location.pathname;
          window.location.href = "/api/login";
        }

        if (window.location.pathname.indexOf("/orgs") === 0) {
          // Pathname in in the form "/orgs/<orgid>/...",
          // so let's extract that as a prefix.
          const orgPath = window.location.pathname
            .split("/")
            .slice(0, 3)
            .join("/");

          if (error.response?.status === 404) {
            if ((error.response.data as NotFoundResponse).resource === "org") {
              // Org does not exist or is not represented by the user.
              if (window.location.pathname !== `${orgPath}/not-found`) {
                window.location.href = `${orgPath}/not-found`;
              }
            }
          } else if (error.response?.status === 402) {
            // no active license
            if (window.location.pathname !== `${orgPath}/order`) {
              window.location.href = `${orgPath}/order`;
            }
          } else if (error.response?.status === 403) {
            // no permission to access organization
            if (window.location.pathname !== `${orgPath}/permission`) {
              window.location.href = `${orgPath}/permission`;
            }
          }
        } else if (window.location.pathname.indexOf("/admin") === 0) {
          if (error.response?.status === 403) {
            window.location.href = "/";
          }
        }
      }
      return Promise.reject(error);
    }
  );

  return rpApi;
};

const rpApi = createRpApi();
export default rpApi;

export const useTopMenu = () => {
  const orgId = useOrgId();
  return useQuery(
    ["getTopMenu", orgId],
    async () => {
      const { data } = await rpApi.get<TopMenuProps["topMenuParameters"]>(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `/api/orgs/${orgId}/top-menu`
      );
      return data;
    },
    {
      enabled: !!orgId,
    }
  );
};

interface UserCompany {
  default: boolean;
  permissions: string[];
  qvarnId: OrgId;
  role: string;
}

interface UserInfo {
  companies: UserCompany[];
  email: string;
  globalPermissions: string[];
  isAdmin: boolean;
  qvarnId: string;
  username: string;
}

export const useUserInfo = () => {
  const userInfoResponse = useQuery(["getUserInfo"], async () => {
    const { data } = await rpApi.get<UserInfo>("/api/user");
    return data;
  });

  if (userInfoResponse.status === "error") {
    // Fatal error, no point to continue.
    throw userInfoResponse.error;
  }

  return userInfoResponse;
};

export const useUserManualReportAccessPermission = () => {
  const userInfo = useUserInfo();
  if (userInfo.isSuccess) {
    return userInfo.data.globalPermissions.includes("rp_admin_manual_reports");
  }
  return false;
};

export enum CompanyAccountStatus {
  ACTIVE = "active",
  NOK = "nok",
  PENDING = "pending",
}

export const useOrgId = () => {
  const params = useParams<{ orgId?: string }>();
  const orgId = useAppSelector(state => state.common.selectedOrgId);
  return params.orgId || orgId;
};

export enum OrgCountry {
  EE = "EE",
  FI = "FI",
  LT = "LT",
  LV = "LV",
  PL = "PL",
  SE = "SE",
}

/**
 * Basic organization info.
 */
interface OrgInfo {
  name: string;
  syncId: string;
  registrationNumber: string;
  country: OrgCountry;
  companyAccountStatus: CompanyAccountStatus;
  role: string | null;
}

export const useOrgInfo = () => {
  const orgId = useOrgId();
  return useQuery(
    ["org", orgId, "info"],
    async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const { data } = await rpApi.get<OrgInfo>(`/api/orgs/${orgId}`);
        return data;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 404) {
            return null;
          }
        }
        throw error;
      }
    },
    {
      enabled: !!orgId,
    }
  );
};

export const useOrgName = () => {
  const intl = useIntl();
  const orgInfoQuery = useOrgInfo();
  if (orgInfoQuery.isSuccess) {
    if (!orgInfoQuery.data?.name) {
      return intl.formatMessage({
        id: "companyNameNotAvailable",
        defaultMessage: "[FI] [yrityksen nimi ei ole saatavilla]",
      });
    }
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `${orgInfoQuery.data.name} - ${orgInfoQuery.data.registrationNumber} [${orgInfoQuery.data.country}]`;
  } else if (orgInfoQuery.isError) {
    throw orgInfoQuery.error;
  }
  return "...";
};

export const useOrgBusinessId = () => {
  const intl = useIntl();
  const orgInfoQuery = useOrgInfo();
  switch (orgInfoQuery.isSuccess) {
    case false:
      return "...";
    case true:
      if (!orgInfoQuery.data?.registrationNumber) {
        return intl.formatMessage({
          id: "companyBusinessIdNotAvailable",
          defaultMessage: "[FI] [yrityksen y-tunnus ei ole saatavilla]",
        });
      }
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      return orgInfoQuery.data.registrationNumber;
  }
};

export type ReportStatus =
  | "ok"
  | "incomplete"
  | "investigate"
  | "attention"
  | "stop";

export interface Report {
  status: ReportStatus;
  statusUpdated: Date;
}

export const useReport = () => {
  const orgId = useOrgId();
  return useQuery(
    ["org", orgId, "report"],
    async () => {
      try {
        const { data } = await rpApi.get<Report | null>(
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `/api/orgs/${orgId}/report`
        );
        return data;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 404) {
            return null;
          }
        }
        throw error;
      }
    },
    {
      enabled: !!orgId,
    }
  );
};

export const useCheckReport = () => {
  const report = useReport();
  if (report.isSuccess) {
    if (report.data && report.data.status !== "ok") {
      return false;
    }
  }
  return true;
};

export enum LicenseStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

interface License {
  startDate: string | null;
  status: LicenseStatus;
}

interface OrderInfo {
  license: License | null;
}

export const useOrderInfo = () => {
  const orgId = useOrgId();
  return useQuery(
    ["org", orgId, "order"],
    async () => {
      const { data } = await rpApi.get<OrderInfo>(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `/api/orgs/${orgId}/order`
      );
      return data;
    },
    {
      enabled: !!orgId,
    }
  );
};

export const useConfig = () => {
  const resp = useQuery("clientEnvironment", async () => {
    const { data } = await rpApi.get<Config>("/api/config");
    return data;
  });

  if (resp.status === "error") {
    // Fatal error, no point to continue.
    throw resp.error;
  }

  return resp;
};
